import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "utils/request";
export const fetchProviders = createAsyncThunk(
  "emailAutomation/fetchProviders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/providers/`,
        { method: "GET" }
      );

      const providers = response?.providers || [];
      const emailProvider = providers.find(
        (p) => p.provider_type !== "virtual" && p.status === "active"
      );

      return {
        providers,
        isConnected: Boolean(emailProvider),
        sender: emailProvider
          ? {
              name: emailProvider.provider_type_display || "",
              email: emailProvider.email_identifier || "",
            }
          : null,
      };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch providers"
      );
    }
  }
);

export const fetchEmailTemplates = createAsyncThunk(
  "emailAutomation/fetchEmailTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/email-automation/templates/`,
        { method: "GET" }
      );

      return response?.results || [];
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch email templates"
      );
    }
  }
);

export const fetchTemplateVariables = createAsyncThunk(
  "emailAutomation/fetchTemplateVariables",
  async (eventType, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/email-automation/variables/?event_type=${eventType}`,
        { method: "GET" }
      );

      const data =
        response?.results || response?.variables || response?.data || response;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch template variables"
      );
    }
  }
);

export const fetchTemplatePreview = createAsyncThunk(
  "emailAutomation/fetchTemplatePreview",
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/email-automation/templates/${templateId}/preview/`,
        { method: "GET" }
      );

      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch template preview"
      );
    }
  }
);

export const refreshTemplatePreview = createAsyncThunk(
  "emailAutomation/refreshTemplatePreview",
  async ({ templateId, subject, body }, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/email-automation/preview/`,
        {
          method: "POST",
          body: JSON.stringify({ template_id: templateId, subject, body }),
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to refresh preview"
      );
    }
  }
);

export const saveEmailTemplate = createAsyncThunk(
  "emailAutomation/saveEmailTemplate",
  async ({ templateId, data }, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/email-automation/templates/${templateId}/`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        }
      );

      return response;
    } catch (error) {
      const message = error?.body
        ? Object.values(error.body).flat().join(" ")
        : error.message || "Failed to save email template";
      return rejectWithValue(message);
    }
  }
);

export const fetchSenders = createAsyncThunk(
  "emailAutomation/fetchSenders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/email-automation/sender/`,
        { method: "GET" }
      );

      return {
        providers: Array.isArray(response?.providers)
          ? response.providers
          : [],
        senderProviderId: response?.sender_provider_id || null,
        hasDisconnectedProvider: response?.has_disconnected_provider || false,
      };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch senders"
      );
    }
  }
);

export const changeSender = createAsyncThunk(
  "emailAutomation/changeSender",
  async ({ providerId }, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/email-automation/sender/`,
        {
          method: "PATCH",
          body: JSON.stringify({ provider_id: providerId }),
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to change sender"
      );
    }
  }
);

const initialState = {
  providers: [],
  isConnected: false,
  sender: null,
  templates: [],
  fetchStatus: "idle",
  fetchError: null,
  templatesFetchStatus: "idle",
  templatesFetchError: null,
  // Sender state
  senders: [],
  senderProviderId: null,
  hasDisconnectedProvider: false,
  sendersFetchStatus: "idle",
  changeSenderStatus: "idle",
  changeSenderError: null,
  // Editor state
  variables: [],
  variablesFetchStatus: "idle",
  preview: null,
  previewFetchStatus: "idle",
  refreshPreviewStatus: "idle",
  saveStatus: "idle",
  saveError: null,
};

const emailAutomationSlice = createSlice({
  name: "emailAutomation",
  initialState,
  reducers: {
    clearEditState: (state) => {
      state.variables = [];
      state.variablesFetchStatus = "idle";
      state.preview = null;
      state.previewFetchStatus = "idle";
      state.refreshPreviewStatus = "idle";
      state.saveStatus = "idle";
      state.saveError = null;
      // Force list page to re-fetch templates on return
      state.templatesFetchStatus = "idle";
    },
    clearSaveStatus: (state) => {
      state.saveStatus = "idle";
      state.saveError = null;
    },
    clearChangeSenderStatus: (state) => {
      state.changeSenderStatus = "idle";
      state.changeSenderError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProviders.pending, (state) => {
        state.fetchStatus = "loading";
        state.fetchError = null;
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.fetchStatus = "success";
        state.providers = action.payload.providers;
        state.isConnected = action.payload.isConnected;
        state.sender = action.payload.sender;
        state.fetchError = null;
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.fetchError = action.payload;
      })
      .addCase(fetchEmailTemplates.pending, (state) => {
        state.templatesFetchStatus = "loading";
        state.templatesFetchError = null;
      })
      .addCase(fetchEmailTemplates.fulfilled, (state, action) => {
        state.templatesFetchStatus = "success";
        state.templates = action.payload;
        state.templatesFetchError = null;
      })
      .addCase(fetchEmailTemplates.rejected, (state, action) => {
        state.templatesFetchStatus = "failed";
        state.templatesFetchError = action.payload;
      })
      .addCase(fetchTemplateVariables.pending, (state) => {
        state.variablesFetchStatus = "loading";
      })
      .addCase(fetchTemplateVariables.fulfilled, (state, action) => {
        state.variablesFetchStatus = "success";
        state.variables = action.payload;
      })
      .addCase(fetchTemplateVariables.rejected, (state) => {
        state.variablesFetchStatus = "failed";
      })
      .addCase(fetchTemplatePreview.pending, (state) => {
        state.previewFetchStatus = "loading";
      })
      .addCase(fetchTemplatePreview.fulfilled, (state, action) => {
        state.previewFetchStatus = "success";
        state.preview = action.payload;
      })
      .addCase(fetchTemplatePreview.rejected, (state) => {
        state.previewFetchStatus = "failed";
      })
      .addCase(refreshTemplatePreview.pending, (state) => {
        state.refreshPreviewStatus = "loading";
      })
      .addCase(refreshTemplatePreview.fulfilled, (state, action) => {
        state.refreshPreviewStatus = "idle";
        state.preview = action.payload;
      })
      .addCase(refreshTemplatePreview.rejected, (state) => {
        state.refreshPreviewStatus = "failed";
      })
      .addCase(saveEmailTemplate.pending, (state) => {
        state.saveStatus = "loading";
        state.saveError = null;
      })
      .addCase(saveEmailTemplate.fulfilled, (state, action) => {
        state.saveStatus = "success";
        const idx = state.templates.findIndex(
          (t) => t.id === action.payload.id
        );
        if (idx !== -1) {
          state.templates[idx] = action.payload;
        }
        state.saveError = null;
      })
      .addCase(saveEmailTemplate.rejected, (state, action) => {
        state.saveStatus = "failed";
        state.saveError = action.payload;
      })
      .addCase(fetchSenders.pending, (state) => {
        state.sendersFetchStatus = "loading";
      })
      .addCase(fetchSenders.fulfilled, (state, action) => {
        state.sendersFetchStatus = "success";
        state.senders = action.payload.providers;
        state.senderProviderId = action.payload.senderProviderId;
        state.hasDisconnectedProvider = action.payload.hasDisconnectedProvider;
      })
      .addCase(fetchSenders.rejected, (state) => {
        state.sendersFetchStatus = "failed";
      })
      .addCase(changeSender.pending, (state) => {
        state.changeSenderStatus = "loading";
        state.changeSenderError = null;
      })
      .addCase(changeSender.fulfilled, (state, action) => {
        state.changeSenderStatus = "success";
        state.changeSenderError = null;
        // Force re-fetch of senders and providers to sync sender display
        state.sendersFetchStatus = "idle";
        state.fetchStatus = "idle";
      })
      .addCase(changeSender.rejected, (state, action) => {
        state.changeSenderStatus = "failed";
        state.changeSenderError = action.payload;
      });
  },
});

export const { clearEditState, clearSaveStatus, clearChangeSenderStatus } =
  emailAutomationSlice.actions;
export default emailAutomationSlice.reducer;
